import { GridColDef } from '@mui/x-data-grid-pro'
import { styleConstants } from '@paywithextend/components'
import { useMutationState } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import { spacing } from 'ext-ui/style/spacing'
import React from 'react'
import { useSelector } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { BackendErrorToastMessage } from 'src/app/Toast/Toast'
import AutomationRulesListEmptyState from 'src/components/AutomationRulesListEmptyState/AutomationRulesListEmptyState'
import Box from 'src/components/Box/Box'
import { chevronColumnDef } from 'src/components/DataGrid/cells/ChevronCell'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import UserAvatarCell from 'src/components/DataGrid/cells/UserAvatarCell/UserAvatarCell'
import DataGrid, { DataGridRowHeight } from 'src/components/DataGrid/DataGrid'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import OnOffSwitch from 'src/components/OnOffSwitch/OnOffSwitch'
import SvgAlertCircledFilled from 'src/components/Svgs/SvgAlertCircledFilled'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import { TagIconWithTooltip } from 'src/components/TagIconWithTooltip/TagIconWithTooltip'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import {
  AutomationRuleFundingSource,
  AutomationRuleListModel,
  AutomationRuleSource,
  AutomationRuleStatusType,
} from 'src/models/automationRules.models'
import { connectedPush } from 'src/navigation/Lnk'
import {
  createAutomationRuleUrl,
  updateAutomationRuleUrl,
} from 'src/navigation/urls'
import {
  AutomationRuleListSortField,
  PatchAutomationRuleVariables,
  useAutomationRuleList,
  usePatchAutomationRule,
} from 'src/queries/organizations/automationRules.queries'
import { automationRulesQueryKeys } from 'src/queries/queries.constants'
import { selectOrganizationIdFromRoute } from 'src/redux/selectors/data/organizations.selectors'
import { selectBrandColorHighlight } from 'src/redux/selectors/session.selectors'
import { BackendErrorPayload } from 'src/utils/error.utils'
import { css } from 'styled-components'

interface AutomationRulesListRow {
  id: string
  index: number
  rule: AutomationRuleListModel
}

interface AutomationRulesListProps {}

const AutomationRulesList: React.FC<
  AutomationRulesListProps
> = (): React.ReactElement => {
  const translate = useTranslate()
  const orgId = useSelector(selectOrganizationIdFromRoute)
  const dispatch = useBackgroundDispatch()

  const brandColorHighlight = useSelector(selectBrandColorHighlight)

  const {
    data: automationRules,
    complete,
    isFetching,
    isPending,
    isSuccess,
  } = useAutomationRuleList({
    filters: {
      sortField: AutomationRuleListSortField.CreatedAt,
      sortDirection: 'DESC',
      fundingSources: [AutomationRuleFundingSource.CreditCard],
    },
  })
  const { mutateAsync: patchAutomationRule } = usePatchAutomationRule()
  const patchAutomationRulePendingMutations = useMutationState({
    filters: { mutationKey: automationRulesQueryKeys.patch() },
    // TS can't know the type just from the mutationKey
    // https://github.com/TanStack/query/issues/6843#issuecomment-1941577758
    select: (mutation) =>
      mutation.state.status === 'pending'
        ? (mutation.state.variables as PatchAutomationRuleVariables | undefined)
        : undefined,
  }).filter(
    (variables): variables is PatchAutomationRuleVariables =>
      variables !== undefined,
  )

  const rows: AutomationRulesListRow[] =
    automationRules?.map((rule, i) => {
      return {
        id: rule.id,
        index: i,
        rule: rule,
      }
    }) ?? []

  const cols: Array<GridColDef<AutomationRulesListRow>> = [
    {
      field: 'createdAt',
      headerName: translate('Created'),
      disableColumnMenu: true,
      sortable: false,
      hideable: false,
      editable: false,
      minWidth: 130,
      maxWidth: 130,
      renderCell: (params): React.ReactElement => (
        <DataGridCell params={params} text={params.row.rule.createdAtString} />
      ),
    },
    {
      field: 'name',
      headerName: translate('Rule Name'),
      disableColumnMenu: true,
      sortable: false,
      hideable: false,
      editable: false,
      flex: 1,
      renderCell: (params): React.ReactElement => {
        const hasInactiveMapping = params.row.rule.mappings.some(
          (mapping) => mapping.expenseLabel?.active === false,
        )
        return (
          <DataGridCell
            params={params}
            text={params.row.rule.name}
            icon={
              hasInactiveMapping ? (
                <TagIconWithTooltip
                  testId={'automationRuleInactiveMappingTag'}
                  tooltipText={translate(
                    'There’s a problem with one or more conditions in this rule.',
                  )}
                  iconBackgroundColor={
                    styleConstants.colors.yellow_background_light
                  }
                  icon={
                    <SvgAlertCircledFilled
                      css={css`
                        width: 16px;
                        height: 16px;
                        transform: scale(0.68);
                        transform-origin: top left;
                        overflow: visible;
                      `}
                      fill={styleConstants.colors.yellow_darkest}
                    />
                  }
                />
              ) : undefined
            }
          />
        )
      },
    },
    {
      field: 'discriminator',
      headerName: translate('Applies To'),
      disableColumnMenu: true,
      sortable: false,
      hideable: false,
      editable: false,
      flex: 1,
      renderCell: (params): React.ReactElement => {
        const transactionSources = params.row.rule.conditions
          .map((condition) => condition.source)
          .filter((item): item is AutomationRuleSource => item !== undefined)

        return (
          <DataGridCell
            params={params}
            text={
              transactionSources.length > 0
                ? transactionSources.map((source, i) => (
                    <span key={`${source.displayName}-${i}`}>
                      <span
                        css={css`
                          padding-right: ${spacing(1)};
                        `}
                      >
                        {source.displayName}
                      </span>
                      {source.last4 && (
                        <Pill
                          css={css`
                            display: inline-flex;
                          `}
                          size={'small'}
                        >
                          {source.last4}
                        </Pill>
                      )}
                      {i !== transactionSources.length - 1 && (
                        <span
                          css={css`
                            padding-right: ${spacing(4)};
                          `}
                        />
                      )}
                    </span>
                  ))
                : translate('All Accounts')
            }
          />
        )
      },
    },
    {
      field: 'ruleCreator',
      headerName: translate('Rule Creator'),
      disableColumnMenu: true,
      sortable: false,
      hideable: false,
      editable: false,
      flex: 1,
      renderCell: (params): React.ReactElement => {
        const creator = params.row.rule.createdByUser
        return <UserAvatarCell user={creator} params={params} />
      },
    },
    {
      field: 'active',
      headerName: translate('Active'),
      description: translate(
        'When active, everyone in the org can code transactions to the category. When inactive, the category will be hidden from view.',
      ),
      disableColumnMenu: true,
      sortable: false,
      hideable: false,
      editable: false,
      minWidth: 120,
      renderCell: (params): React.ReactElement => {
        return (
          <DataGridCellWrapper params={params}>
            <OnOffSwitch
              testId={'isActiveSwitch'}
              onBgColor={brandColorHighlight}
              smallMode
              defaultOnOffState={
                params.row.rule.status === AutomationRuleStatusType.Active
              }
              disabled={
                patchAutomationRulePendingMutations.find(
                  (variables) => variables.id === params.row.id,
                ) !== undefined
              }
              onChange={(status): Promise<boolean> =>
                patchAutomationRule({
                  id: params.row.id,
                  body: {
                    status: status
                      ? AutomationRuleStatusType.Active
                      : AutomationRuleStatusType.Inactive,
                  },
                })
                  .then(() => true)
                  .catch((e: AxiosError<BackendErrorPayload<string>>) => {
                    dispatch(
                      createSetIsToastOpen(true, {
                        toastMessage: <BackendErrorToastMessage error={e} />,
                        failureColorScheme: true,
                        hideImage: true,
                      }),
                    )
                    return false
                  })
              }
            />
          </DataGridCellWrapper>
        )
      },
    },
    chevronColumnDef,
  ]

  if (isPending) return <LoadingBackgroundPage />
  return automationRules?.length === 0 && isSuccess ? (
    <AutomationRulesListEmptyState />
  ) : (
    <Box
      css={css`
        width: 100%;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      `}
      noPadding
      noShadow
      outlineBorder
    >
      <div
        css={css`
          display: flex;
          width: 100%;
          justify-content: space-between;
          flex-direction: row;
          padding: 24px 28px;
          gap: 12px;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            gap: 12px;
          `}
        ></div>
        <Button
          variant={'toolbar'}
          dropShadow={false}
          data-testid={'addAutomationRuleBtn'}
          onClick={(): void => {
            dispatch(
              connectedPush({
                pathname: createAutomationRuleUrl(orgId),
                search: '',
                hash: '',
                state: {},
              }),
            )
          }}
        >
          {translate('New Rule +')}
        </Button>
      </div>
      {!isFetching && automationRules?.length === 0 ? (
        <TableEmptyState design={TableEmptyStateDesigns.ExpenseCategories} />
      ) : (
        <DataGrid
          filtersBody={{}}
          rows={rows}
          rowHeight={DataGridRowHeight.Default}
          columns={cols}
          disableColumnFilter
          disableColumnReorder
          hideFooter
          onRowClick={(row): void => {
            dispatch(
              connectedPush({
                pathname: updateAutomationRuleUrl(orgId, row.id.toString()),
                search: '',
                hash: '',
                state: {},
              }),
            )
          }}
          infiniteScrollProps={{
            fetch: (): void => {
              return
            },
            loading: isFetching,
            complete: complete,
          }}
          slotProps={{
            loadingOverlay: {
              // workaround for having non-0 height when loading on a non-fixed height datagrid
              noRowsVariant: 'skeleton',
            },
          }}
        />
      )}
    </Box>
  )
}

export default AutomationRulesList
